import { api } from '@/services/api';

// ==================== TYPES ====================

export type WalkthroughStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type WalkthroughStepType = 'spotlight' | 'modal';
export type WalkthroughPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';
export type WalkthroughTriggerType =
  | 'first_login'
  | 'days_after_signup'
  | 'route_visited'
  | 'manual';

export interface WalkthroughStepConfig {
  waitForSelectorMs?: number;
  scrollIntoView?: boolean;
  advanceOnClick?: boolean;
  ctaLabel?: string;
  ctaHref?: string;
}

export interface WalkthroughStep {
  id: string;
  walkthroughId: string;
  order: number;
  type: WalkthroughStepType;
  title: string;
  description: string;
  imageUrl?: string | null;
  route?: string | null;
  anchorKey?: string | null;
  targetSelector?: string | null;
  placement: WalkthroughPlacement;
  padding: number;
  config?: WalkthroughStepConfig | null;
  createdAt: string;
  updatedAt: string;
}

export interface WalkthroughTrigger {
  id: string;
  walkthroughId: string;
  type: WalkthroughTriggerType;
  config?: { days?: number; route?: string } | null;
  isActive: boolean;
  createdAt: string;
}

export interface UserWalkthroughProgress {
  id: string;
  userId: string;
  walkthroughId: string;
  status: 'in_progress' | 'completed' | 'skipped';
  currentStep: number;
  seenVersion: number;
  startedAt: string;
  completedAt?: string | null;
  dismissedAt?: string | null;
}

export interface Walkthrough {
  id: string;
  name: string;
  description?: string | null;
  status: WalkthroughStatus;
  version: number;
  allowSkip: boolean;
  showProgress: boolean;
  roles: string[];
  tiers: string[];
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt: string;
  updatedAt: string;
  steps?: WalkthroughStep[];
  triggers?: WalkthroughTrigger[];
  /** Presente em /me/pending: progresso do usuário, para retomar o tour. */
  userProgress?: UserWalkthroughProgress | null;
  _count?: { steps: number; progress: number };
}

export interface WalkthroughListMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface WalkthroughListResult {
  data: Walkthrough[];
  meta: WalkthroughListMeta;
}

export type CreateWalkthroughInput = {
  name: string;
  description?: string;
  status?: WalkthroughStatus;
  version?: number;
  allowSkip?: boolean;
  showProgress?: boolean;
  roles?: string[];
  tiers?: string[];
  startsAt?: string | null;
  endsAt?: string | null;
};

export type UpdateWalkthroughInput = Partial<CreateWalkthroughInput>;

export type CreateStepInput = {
  type?: WalkthroughStepType;
  title: string;
  description: string;
  imageUrl?: string;
  route?: string;
  anchorKey?: string;
  targetSelector?: string;
  placement?: WalkthroughPlacement;
  padding?: number;
  order?: number;
  config?: WalkthroughStepConfig;
};

export type UpdateStepInput = Partial<CreateStepInput>;

export type CreateTriggerInput = {
  type: WalkthroughTriggerType;
  config?: { days?: number; route?: string };
  isActive?: boolean;
};

function unwrap<T>(data: unknown): T {
  return ((data as { data?: T })?.data ?? data) as T;
}

// ==================== SERVICE ====================

class WalkthroughService {
  // ----- Admin -----

  async list(params: {
    page?: number;
    perPage?: number;
    status?: string;
    search?: string;
  }): Promise<WalkthroughListResult> {
    const { data } = await api.get('/walkthrough', { params });
    // O TransformInterceptor ACHATA respostas paginadas:
    // { success, data: Walkthrough[], meta }  — e não { data: { data, meta } }
    const items = data?.data ?? data;
    return {
      data: Array.isArray(items) ? items : (items?.data ?? []),
      meta: data?.meta ?? {
        total: 0,
        page: params.page ?? 1,
        perPage: params.perPage ?? 20,
        totalPages: 1,
      },
    };
  }

  async getById(id: string): Promise<Walkthrough> {
    const { data } = await api.get(`/walkthrough/admin/${id}`);
    return unwrap<Walkthrough>(data);
  }

  async create(input: CreateWalkthroughInput): Promise<Walkthrough> {
    const { data } = await api.post('/walkthrough', input);
    return unwrap<Walkthrough>(data);
  }

  async update(
    id: string,
    input: UpdateWalkthroughInput,
  ): Promise<Walkthrough> {
    const { data } = await api.put(`/walkthrough/${id}`, input);
    return unwrap<Walkthrough>(data);
  }

  async remove(id: string): Promise<void> {
    await api.delete(`/walkthrough/${id}`);
  }

  async duplicate(id: string): Promise<Walkthrough> {
    const { data } = await api.post(`/walkthrough/${id}/duplicate`);
    return unwrap<Walkthrough>(data);
  }

  // ----- Steps -----

  async addStep(
    walkthroughId: string,
    input: CreateStepInput,
  ): Promise<WalkthroughStep> {
    const { data } = await api.post(
      `/walkthrough/${walkthroughId}/steps`,
      input,
    );
    return unwrap<WalkthroughStep>(data);
  }

  async updateStep(
    stepId: string,
    input: UpdateStepInput,
  ): Promise<WalkthroughStep> {
    const { data } = await api.put(`/walkthrough/steps/${stepId}`, input);
    return unwrap<WalkthroughStep>(data);
  }

  async deleteStep(stepId: string): Promise<void> {
    await api.delete(`/walkthrough/steps/${stepId}`);
  }

  async reorderSteps(walkthroughId: string, ids: string[]): Promise<void> {
    await api.put(`/walkthrough/${walkthroughId}/steps/reorder`, { ids });
  }

  // ----- Triggers -----

  async addTrigger(
    walkthroughId: string,
    input: CreateTriggerInput,
  ): Promise<WalkthroughTrigger> {
    const { data } = await api.post(
      `/walkthrough/${walkthroughId}/triggers`,
      input,
    );
    return unwrap<WalkthroughTrigger>(data);
  }

  async deleteTrigger(triggerId: string): Promise<void> {
    await api.delete(`/walkthrough/triggers/${triggerId}`);
  }

  // ----- Aluno -----

  async getMyPending(): Promise<Walkthrough[]> {
    const { data } = await api.get('/walkthrough/me/pending');
    return unwrap<Walkthrough[]>(data) || [];
  }

  async getPublishedById(id: string): Promise<Walkthrough> {
    const { data } = await api.get(`/walkthrough/${id}`);
    return unwrap<Walkthrough>(data);
  }

  async saveProgress(id: string, currentStep: number): Promise<void> {
    await api.post(`/walkthrough/${id}/progress`, { currentStep });
  }

  async complete(id: string): Promise<void> {
    await api.post(`/walkthrough/${id}/complete`);
  }

  async skip(id: string): Promise<void> {
    await api.post(`/walkthrough/${id}/skip`);
  }

  async resetMyProgress(walkthroughId?: string): Promise<void> {
    await api.post(
      '/walkthrough/me/reset',
      {},
      { params: walkthroughId ? { walkthroughId } : undefined },
    );
  }
}

export const walkthroughService = new WalkthroughService();
